"use client";

import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  getQuoteListServerSnapshot,
  getQuoteListSnapshot,
  MAX_QUOTE_LIST_ITEMS,
  normalizeQuoteList,
  parseQuoteListSnapshot,
  subscribeToQuoteList,
  writeQuoteList,
} from "@/lib/quote-list";

export function useQuoteList() {
  const snapshot = useSyncExternalStore(
    subscribeToQuoteList,
    getQuoteListSnapshot,
    getQuoteListServerSnapshot,
  );
  const slugs = useMemo(() => parseQuoteListSnapshot(snapshot), [snapshot]);

  const replace = useCallback((nextSlugs: readonly string[]) => {
    return writeQuoteList(nextSlugs);
  }, []);

  const add = useCallback(
    (slug: string) => {
      const current = parseQuoteListSnapshot(getQuoteListSnapshot());
      if (current.includes(slug)) return { slugs: current, added: false, full: false };
      if (current.length >= MAX_QUOTE_LIST_ITEMS) {
        return { slugs: current, added: false, full: true };
      }
      const next = writeQuoteList([...current, slug]);
      return { slugs: next, added: true, full: false };
    },
    [],
  );

  const addMany = useCallback((incoming: readonly string[]) => {
    const current = parseQuoteListSnapshot(getQuoteListSnapshot());
    const candidates = normalizeQuoteList([...current, ...incoming]);
    const next = writeQuoteList(candidates);
    return {
      slugs: next,
      added: Math.max(0, next.length - current.length),
      full:
        next.length >= MAX_QUOTE_LIST_ITEMS &&
        incoming.some((slug) => !next.includes(slug)),
    };
  }, []);

  const remove = useCallback((slug: string) => {
    const current = parseQuoteListSnapshot(getQuoteListSnapshot());
    return writeQuoteList(current.filter((candidate) => candidate !== slug));
  }, []);

  const toggle = useCallback((slug: string) => {
    const current = parseQuoteListSnapshot(getQuoteListSnapshot());
    if (current.includes(slug)) {
      return {
        slugs: writeQuoteList(current.filter((candidate) => candidate !== slug)),
        added: false,
        full: false,
      };
    }
    if (current.length >= MAX_QUOTE_LIST_ITEMS) {
      return { slugs: current, added: false, full: true };
    }
    return {
      slugs: writeQuoteList([...current, slug]),
      added: true,
      full: false,
    };
  }, []);

  const clear = useCallback(() => writeQuoteList([]), []);

  return { slugs, replace, add, addMany, remove, toggle, clear };
}
